/** Valor YYYY-MM compatível com input[type="month"]. */
export function getCurrentCompetenceMonthValue(referenceDate: Date = new Date()): string {
  const year = referenceDate.getFullYear();
  const month = String(referenceDate.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

const BRAZIL_TIME_ZONE = 'America/Sao_Paulo';

/** `YYYY-MM` for the current calendar month in Brazil (Performance launch modal). */
export function getBrazilCurrentCompetenceMonth(reference: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: BRAZIL_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(reference);

  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;

  if (year && month) {
    return `${year}-${month}`;
  }

  return getCurrentCompetenceMonthValue(reference);
}

/** Keeps YYYY-MM at the current month or earlier. */
export function clampCompetenceMonthToCurrent(
  monthValue: string,
  referenceDate: Date = new Date(),
): string {
  const current = getCurrentCompetenceMonthValue(referenceDate);
  return /^\d{4}-\d{2}$/.test(monthValue) && monthValue <= current
    ? monthValue
    : current;
}

/** Competência anterior (mês calendário imediatamente anterior). */
export function getPreviousCompetenceMonthValue(referenceDate: Date = new Date()): string {
  const previousMonthDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - 1, 1);
  const year = previousMonthDate.getFullYear();
  const month = String(previousMonthDate.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Dado um valor YYYY-MM do filtro, retorna o mês anterior.
 * Orquestrador e Performance: o filtro é o mês de trabalho; a competência
 * gravada / consultada em workout_assignments é o mês anterior.
 */
export function getPreviousCompetenceMonthFromValue(monthValue: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(monthValue.trim());
  if (!match) {
    return monthValue;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return monthValue;
  }

  return getPreviousCompetenceMonthValue(new Date(year, month - 1, 1));
}

/** Converte YYYY-MM para a data ISO de competência (1º dia do mês). */
export function competenceMonthToIsoDate(monthValue: string): string {
  return `${monthValue}-01`;
}

/** Label MM/YYYY a partir de MMYYYY, YYYYMM, YYYY-MM ou YYYY-MM-DD. */
export function formatTrainingPeriodLabel(trainingPeriod: string): string {
  const trimmed = trainingPeriod.trim();

  if (/^\d{2}\/\d{4}$/.test(trimmed)) {
    return trimmed;
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const [year, month] = trimmed.slice(0, 10).split('-');
    return `${month}/${year}`;
  }

  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    const [year, month] = trimmed.split('-');
    return `${month}/${year}`;
  }

  if (!/^\d{6}$/.test(trimmed)) {
    return trimmed;
  }

  const monthPrefix = Number(trimmed.slice(0, 2));
  const yearSuffix = Number(trimmed.slice(2, 6));
  if (monthPrefix >= 1 && monthPrefix <= 12 && yearSuffix >= 1900 && yearSuffix <= 2100) {
    return `${trimmed.slice(0, 2)}/${trimmed.slice(2)}`;
  }

  const yearPrefix = Number(trimmed.slice(0, 4));
  const monthSuffix = Number(trimmed.slice(4, 6));
  if (yearPrefix >= 1900 && yearPrefix <= 2100 && monthSuffix >= 1 && monthSuffix <= 12) {
    return `${trimmed.slice(4, 6)}/${trimmed.slice(0, 4)}`;
  }

  return trimmed;
}

/** ISO date (YYYY-MM-DD) of the previous competence month. */
export function previousCompetenceIsoDate(competence: string): string {
  return competenceMonthToIsoDate(getPreviousCompetenceMonthFromValue(competence.slice(0, 7)));
}

/** Calendar date in America/Sao_Paulo, optionally shifted by whole days. */
export function getBrazilCivilDate(reference: Date = new Date(), offsetDays = 0): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: BRAZIL_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(reference);
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;

  if (!year || !month || !day) {
    return formatLocalIsoDate(reference);
  }

  if (offsetDays === 0) {
    return `${year}-${month}-${day}`;
  }

  const shifted = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day) + offsetDays));
  const shiftedYear = shifted.getUTCFullYear();
  const shiftedMonth = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const shiftedDay = String(shifted.getUTCDate()).padStart(2, '0');
  return `${shiftedYear}-${shiftedMonth}-${shiftedDay}`;
}

/** First and last civil day of YYYY-MM. */
export function getBrazilMonthBounds(month: string): { start: string; end: string } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const monthNumber = Number(match[2]);
  if (monthNumber < 1 || monthNumber > 12) {
    return null;
  }

  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return {
    start: `${match[1]}-${match[2]}-01`,
    end: `${match[1]}-${match[2]}-${String(lastDay).padStart(2, '0')}`,
  };
}

/** Calendar month immediately before the current month in Brazil. */
export function getBrazilPreviousCompetenceMonth(reference: Date = new Date()): string {
  return getPreviousCompetenceMonthFromValue(getBrazilCurrentCompetenceMonth(reference));
}

function formatLocalIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Inclusive São Paulo calendar range for a competence month.
 * The current month is cut at today so it matches the commercial dashboard.
 */
export function competenceMonthToDateRange(
  monthValue: string,
  referenceDate: Date = new Date(),
): { start: string; end: string } {
  const match = /^(\d{4})-(\d{2})$/.exec(monthValue.trim());
  if (!match) {
    const fallback = getCurrentCompetenceMonthValue(referenceDate);
    return competenceMonthToDateRange(fallback, referenceDate);
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const start = `${match[1]}-${match[2]}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const endOfMonth = `${match[1]}-${match[2]}-${String(lastDay).padStart(2, '0')}`;
  const today = formatLocalIsoDate(referenceDate);
  const isCurrentMonth = monthValue === getCurrentCompetenceMonthValue(referenceDate);

  return {
    start,
    end: isCurrentMonth && today < endOfMonth ? today : endOfMonth,
  };
}
